import { Autocomplete, Checkbox, Popper, TextField } from "@mui/material";

/** Mesmo sx dos filtros de Usuários (autocompleteSx) — ver src/pages/Usuarios/index.js. */
export const FILTER_FIELD_SX = {
  width: "220px",
  flexShrink: 0,
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    "& fieldset": { borderColor: "divider" },
  },
  "& .MuiInputLabel-root": {
    color: "#3c4043",
    fontSize: "0.875rem",
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#3c4043",
  },
};

/**
 * Lista aberta acompanha o texto das opções (mín. a largura do campo), em vez de cortar
 * ou quebrar rótulos longos dentro de um campo estreito.
 */
export function PopperAjustado(props) {
  return (
    <Popper
      {...props}
      placement="bottom-start"
      style={{ ...props.style, width: "fit-content", minWidth: props.anchorEl?.clientWidth, maxWidth: "min(480px, 90vw)" }}
    />
  );
}

/**
 * Autocomplete de opção única (sem digitação livre), no mesmo padrão visual dos
 * filtros da tela de Usuários — options[0] é sempre a opção "Todas/Todos" (value: "").
 *
 * `width`: opcional — vira a largura MÍNIMA do campo (flex-basis), e o campo cresce
 * (flex-grow:1) pra ocupar o espaço sobrando na linha, em vez de deixar vazio depois do
 * último filtro. Usado quando uma aba tem filtros demais pra caber com a largura fixa
 * padrão (ex.: ICMS-ST). Sem essa prop, nenhum consumidor existente muda (width fixo,
 * sem crescer).
 */
export default function SelectFiltro({ label, value, onChange, options, width, ariaLabel }) {
  const selecionado = options.find((o) => o.value === value) ?? options[0] ?? null;
  const sx = width
    ? { ...FILTER_FIELD_SX, width: "auto", minWidth: width, flexGrow: 1, flexShrink: 1, flexBasis: 0 }
    : FILTER_FIELD_SX;
  return (
    <Autocomplete
      size="small"
      disableClearable
      value={selecionado}
      onChange={(_, opt) => onChange(opt?.value ?? "")}
      options={options}
      getOptionLabel={(o) => o?.label ?? ""}
      isOptionEqualToValue={(a, b) => a?.value === b?.value}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          inputProps={ariaLabel ? { ...params.inputProps, "aria-label": ariaLabel } : params.inputProps}
        />
      )}
      PopperComponent={PopperAjustado}
      sx={sx}
    />
  );
}

const OPCAO_TODOS = { value: "__todos__", label: "Todos" };

/**
 * Autocomplete de múltipla escolha com checkbox por opção (a lista fica aberta enquanto o
 * usuário marca). A 1ª opção "Todos" marca/desmarca todas as demais; nada marcado mostra o
 * placeholder "Todos", como os dropdowns de opção única. `value` é a lista de `value`s marcados. A caixa
 * ocupa `width` (padrão: toda a largura do contêiner) e o painel de opções tem exatamente a
 * mesma largura da caixa; textos longos quebram em mais de uma linha.
 */
export function MultiSelectFiltro({ label, value, onChange, options, width = "100%", ariaLabel }) {
  const marcados = Array.isArray(value) ? value : [];
  const selecionadas = options.filter((o) => marcados.includes(o.value));
  const todos = options.length > 0 && selecionadas.length === options.length;
  return (
    <Autocomplete
      multiple
      size="small"
      disableCloseOnSelect
      value={todos ? [OPCAO_TODOS, ...selecionadas] : selecionadas}
      onChange={(_, opts, __, detalhe) => {
        if (detalhe?.option?.value === OPCAO_TODOS.value) {
          onChange(todos ? [] : options.map((o) => o.value));
          return;
        }
        onChange(opts.filter((o) => o !== OPCAO_TODOS).map((o) => o.value));
      }}
      options={[OPCAO_TODOS, ...options]}
      getOptionLabel={(o) => o?.label ?? ""}
      isOptionEqualToValue={(a, b) => a?.value === b?.value}
      renderTags={() =>
        todos
          ? "Todos"
          : selecionadas.length === 1
            ? selecionadas[0].label
            : `${selecionadas.length} opções selecionadas`
      }
      renderOption={(props, option, { selected }) => (
        <li {...props} key={option.value}>
          <Checkbox size="small" checked={selected} sx={{ mr: 1, p: 0.5 }} />
          {option.label}
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={selecionadas.length ? "" : "Todos"}
          InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
          inputProps={ariaLabel ? { ...params.inputProps, "aria-label": ariaLabel } : params.inputProps}
        />
      )}
      // texto das opções marcadas alinhado ao dos dropdowns de opção única (14px do contorno)
      sx={{
        ...FILTER_FIELD_SX,
        width,
        maxWidth: "100%",
        "& .MuiOutlinedInput-root.MuiInputBase-sizeSmall": { paddingLeft: "14px" },
      }}
    />
  );
}

/**
 * Campo de busca livre (texto) na mesma largura/estilo do SelectFiltro — usado nos filtros
 * que o protótipo mostra como caixa de digitação ("CNPJ ou Razão Social", "NCM ou EAN")
 * em vez de select. `width`: mesmo opt-in de SelectFiltro acima.
 */
export function BuscaFiltro({ label, placeholder, value, onChange, width, ariaLabel }) {
  const sx = width
    ? { ...FILTER_FIELD_SX, width: "auto", minWidth: width, flexGrow: 1, flexShrink: 1, flexBasis: 0 }
    : FILTER_FIELD_SX;
  return (
    <TextField
      size="small"
      label={label}
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      inputProps={ariaLabel ? { "aria-label": ariaLabel } : undefined}
      sx={sx}
    />
  );
}
